import React, { useState, useEffect } from 'react';
import { exportDatabase, getBettableByRound, setBettableByRound, getGeneralBetsBettable, setGeneralBetsBettable, triggerGeneralBetsReminder, downloadIncompleteGeneralBetsCSV, getPageVisibility, setPageVisibility, getSignupEnabled, setSignupEnabled, getMatchUpdateInterval, setMatchUpdateInterval, getThirdPlaceQualifyingCount, setThirdPlaceQualifyingCount } from '../../api/generalApi';
import Layout from '../../components/Layout';

// Map between display labels and database round values
const ROUND_MAP: { [key: string]: string } = {
  'Group Stage': 'GROUP',
  'Round of 32': 'ROUND_OF_32',
  'Round of 16': 'ROUND_OF_16',
  'Quarter Finals': 'QUARTER_FINAL',
  'Semi Finals': 'SEMI_FINAL',
  'Third Place': 'THIRD_PLACE',
  'Finals': 'FINAL',
};

const GeneralAdminPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [togglesLoading, setTogglesLoading] = useState(true);
  const [generalBetsBettable, setGeneralBetsBettableState] = useState(false);
  const [generalBetsLoading, setGeneralBetsLoading] = useState(true);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);

  const PAGE_LABELS: { [key: string]: string } = {
    tournament_stats: 'סטטיסטיקות',
    general_bets_summary: 'סיכום כלליים',
    fun_facts: 'Fun Facts',
    side_bets: 'בית צדדי',
  };

  const [pageVisibility, setPageVisibilityState] = useState<{ [key: string]: boolean }>({
    tournament_stats: true,
    general_bets_summary: true,
    fun_facts: true,
    side_bets: true,
  });
  const [pageVisibilityLoading, setPageVisibilityLoading] = useState(true);

  const [signupEnabled, setSignupEnabledState] = useState(true);
  const [signupLoading, setSignupLoading] = useState(true);

  const [matchUpdateInterval, setMatchUpdateIntervalState] = useState(2);
  const [matchUpdateIntervalLoading, setMatchUpdateIntervalLoading] = useState(true);
  const [matchUpdateIntervalSaving, setMatchUpdateIntervalSaving] = useState(false);

  const [thirdPlaceCount, setThirdPlaceCountState] = useState(4);
  const [thirdPlaceCountLoading, setThirdPlaceCountLoading] = useState(true);
  const [thirdPlaceCountSaving, setThirdPlaceCountSaving] = useState(false);

  // State for the 7 stages/categories
  const [toggles, setToggles] = useState<{ [key: string]: boolean }>({
    'Group Stage': false,
    'Round of 32': false,
    'Round of 16': false,
    'Quarter Finals': false,
    'Semi Finals': false,
    'Third Place': false,
    'Finals': false,
  });

  // Fetch current bettable status on mount
  useEffect(() => {
    const fetchBettableStatus = async () => {
      try {
        setTogglesLoading(true);
        const status = await getBettableByRound();
        
        // Map database round values to display labels
        const newToggles: { [key: string]: boolean } = {};
        Object.keys(ROUND_MAP).forEach((label) => {
          const round = ROUND_MAP[label];
          newToggles[label] = status[round] || false;
        });
        
        setToggles(newToggles);
      } catch (err) {
        console.error('Failed to fetch bettable status:', err);
        alert('Failed to load bettable status');
      } finally {
        setTogglesLoading(false);
      }
    };

    fetchBettableStatus();

    const fetchGeneralBetsBettable = async () => {
      try {
        setGeneralBetsLoading(true);
        const bettable = await getGeneralBetsBettable();
        setGeneralBetsBettableState(bettable);
      } catch (err) {
        console.error('Failed to fetch general bets bettable status:', err);
      } finally {
        setGeneralBetsLoading(false);
      }
    };

    fetchGeneralBetsBettable();

    const fetchPageVisibility = async () => {
      try {
        setPageVisibilityLoading(true);
        const visibility = await getPageVisibility();
        setPageVisibilityState(visibility);
      } catch (err) {
        console.error('Failed to fetch page visibility:', err);
      } finally {
        setPageVisibilityLoading(false);
      }
    };

    fetchPageVisibility();

    const fetchSignupEnabled = async () => {
      try {
        setSignupLoading(true);
        const enabled = await getSignupEnabled();
        setSignupEnabledState(enabled);
      } catch (err) {
        console.error('Failed to fetch signup status:', err);
      } finally {
        setSignupLoading(false);
      }
    };

    fetchSignupEnabled();

    const fetchMatchUpdateInterval = async () => {
      try {
        const minutes = await getMatchUpdateInterval();
        setMatchUpdateIntervalState(minutes);
      } catch (err) {
        console.error('Failed to fetch match update interval:', err);
      } finally {
        setMatchUpdateIntervalLoading(false);
      }
    };

    fetchMatchUpdateInterval();

    const fetchThirdPlaceCount = async () => {
      try {
        const count = await getThirdPlaceQualifyingCount();
        setThirdPlaceCountState(count);
      } catch (err) {
        console.error('Failed to fetch third place qualifying count:', err);
      } finally {
        setThirdPlaceCountLoading(false);
      }
    };

    fetchThirdPlaceCount();
  }, []);

  const handleThirdPlaceCountSave = async () => {
    if (thirdPlaceCount <= 0) return;
    setThirdPlaceCountSaving(true);
    try {
      await setThirdPlaceQualifyingCount(thirdPlaceCount);
    } catch (err) {
      console.error('Failed to save third place qualifying count:', err);
      alert('Failed to save count. Please try again.');
    } finally {
      setThirdPlaceCountSaving(false);
    }
  };

  const handleToggle = async (name: string, newState: boolean) => {
    const round = ROUND_MAP[name];
    if (!round) {
      console.error(`Unknown round for label: ${name}`);
      return;
    }

    // Optimistically update UI
    setToggles(prev => ({
      ...prev,
      [name]: newState
    }));

    try {
      await setBettableByRound(round, newState);
    } catch (err) {
      console.error(`Failed to set bettable for ${name}:`, err);
      alert(`Failed to update ${name}. Please try again.`);
      // Revert on error
      setToggles(prev => ({
        ...prev,
        [name]: !newState
      }));
    }
  };

  const handleGeneralBetsToggle = async (newState: boolean) => {
    setGeneralBetsBettableState(newState);
    try {
      await setGeneralBetsBettable(newState);
    } catch (err) {
      console.error('Failed to set general bets bettable:', err);
      alert('Failed to update general bets status. Please try again.');
      setGeneralBetsBettableState(!newState);
    }
  };

  const handleSendReminder = async () => {
    if (!window.confirm('Send general bets reminder to all users with incomplete bets?')) return;
    setReminderLoading(true);
    try {
      await triggerGeneralBetsReminder();
      alert('Reminder sent successfully.');
    } catch (err) {
      console.error('Failed to trigger general bets reminder:', err);
      alert('Failed to send reminder. Please try again.');
    } finally {
      setReminderLoading(false);
    }
  };

  const handleDownloadIncompleteCSV = async () => {
    setCsvLoading(true);
    try {
      await downloadIncompleteGeneralBetsCSV();
    } catch (err) {
      console.error('Failed to download incomplete general bets CSV:', err);
      alert('Failed to download CSV. Please try again.');
    } finally {
      setCsvLoading(false);
    }
  };

  const handleSignupToggle = async (newState: boolean) => {
    setSignupEnabledState(newState);
    try {
      await setSignupEnabled(newState);
    } catch (err) {
      console.error('Failed to set signup status:', err);
      setSignupEnabledState(!newState);
    }
  };

  const handleMatchUpdateIntervalSave = async () => {
    if (matchUpdateInterval <= 0) return;
    setMatchUpdateIntervalSaving(true);
    try {
      await setMatchUpdateInterval(matchUpdateInterval);
    } catch (err) {
      console.error('Failed to save match update interval:', err);
      alert('Failed to save interval. Please try again.');
    } finally {
      setMatchUpdateIntervalSaving(false);
    }
  };

  const handlePageVisibilityToggle = async (pageKey: string, newState: boolean) => {
    setPageVisibilityState(prev => ({ ...prev, [pageKey]: newState }));
    try {
      await setPageVisibility(pageKey, newState);
    } catch (err) {
      console.error(`Failed to set visibility for ${pageKey}:`, err);
      setPageVisibilityState(prev => ({ ...prev, [pageKey]: !newState }));
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const blob = await exportDatabase();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'database_export.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">General</h1>
        </div>

        {/* Bettable toggles — rounds + general bets in one row */}
        <section className="mb-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
            What is bettable
          </h2>
          <div className="flex flex-wrap gap-3">
            {/* General Bets toggle */}
            <button
              onClick={() => handleGeneralBetsToggle(!generalBetsBettable)}
              disabled={generalBetsLoading}
              className={`py-2 px-3 rounded text-sm font-medium transition-all border ${
                generalBetsBettable
                  ? 'bg-green-600 border-green-500 text-white shadow-lg shadow-green-900/20'
                  : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
              } ${generalBetsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              General Bets
              <span className="block text-[10px] opacity-70">
                {generalBetsLoading ? 'Loading...' : (generalBetsBettable ? 'Enabled' : 'Disabled')}
              </span>
            </button>

            {Object.keys(toggles).map((label) => (
              <button
                key={label}
                onClick={() => handleToggle(label, !toggles[label])}
                disabled={togglesLoading}
                className={`py-2 px-3 rounded text-sm font-medium transition-all border ${
                  toggles[label]
                    ? 'bg-green-600 border-green-500 text-white shadow-lg shadow-green-900/20'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
                } ${togglesLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {label}
                <span className="block text-[10px] opacity-70">
                  {togglesLoading ? 'Loading...' : (toggles[label] ? 'Enabled' : 'Disabled')}
                </span>
              </button>
            ))}
          </div>
        </section>

        <hr className="border-gray-800 mb-8" />

        {/* Page visibility toggles */}
        <section className="mb-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
            Page Visibility
          </h2>
          <div className="flex flex-wrap gap-3">
            {Object.entries(PAGE_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => handlePageVisibilityToggle(key, !pageVisibility[key])}
                disabled={pageVisibilityLoading}
                className={`py-2 px-3 rounded text-sm font-medium transition-all border ${
                  pageVisibility[key]
                    ? 'bg-green-600 border-green-500 text-white shadow-lg shadow-green-900/20'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
                } ${pageVisibilityLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {label}
                <span className="block text-[10px] opacity-70">
                  {pageVisibilityLoading ? 'Loading...' : (pageVisibility[key] ? 'Visible' : 'Hidden')}
                </span>
              </button>
            ))}

            <button
              onClick={() => handleSignupToggle(!signupEnabled)}
              disabled={signupLoading}
              className={`py-2 px-3 rounded text-sm font-medium transition-all border ${
                signupEnabled
                  ? 'bg-green-600 border-green-500 text-white shadow-lg shadow-green-900/20'
                  : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
              } ${signupLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              Sign Up
              <span className="block text-[10px] opacity-70">
                {signupLoading ? 'Loading...' : (signupEnabled ? 'Open' : 'Closed')}
              </span>
            </button>
          </div>
        </section>

        <hr className="border-gray-800 mb-8" />

        {/* Notifications */}
        <section className="mb-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
            Notifications
          </h2>
          <div className="flex gap-3">
            <button
              onClick={handleSendReminder}
              disabled={reminderLoading}
              className="py-2 px-4 rounded text-sm font-medium border bg-yellow-600 border-yellow-500 text-white hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {reminderLoading ? 'Sending...' : 'Send General Bets Reminder'}
            </button>
            <button
              onClick={handleDownloadIncompleteCSV}
              disabled={csvLoading}
              className="py-2 px-4 rounded text-sm font-medium border bg-gray-700 border-gray-600 text-white hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {csvLoading ? 'Downloading...' : 'View Incomplete'}
            </button>
          </div>
        </section>

        <hr className="border-gray-800 mb-8" />

        {/* Match Update Interval */}
        <section className="mb-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
            Match Update Interval
          </h2>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              value={matchUpdateIntervalLoading ? '' : matchUpdateInterval}
              disabled={matchUpdateIntervalLoading}
              onChange={e => setMatchUpdateIntervalState(parseInt(e.target.value, 10) || 1)}
              className="w-24 bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-sm disabled:opacity-50"
            />
            <span className="text-gray-400 text-sm">minutes</span>
            <button
              onClick={handleMatchUpdateIntervalSave}
              disabled={matchUpdateIntervalLoading || matchUpdateIntervalSaving || matchUpdateInterval <= 0}
              className="py-2 px-4 rounded text-sm font-medium border bg-blue-600 border-blue-500 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {matchUpdateIntervalSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-2">Takes effect when the next game's routine starts (before kickoff).</p>
        </section>

        <hr className="border-gray-800 mb-8" />

        {/* Third Place Qualifying Count */}
        <section className="mb-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
            Third Place Qualifying Count
          </h2>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              value={thirdPlaceCountLoading ? '' : thirdPlaceCount}
              disabled={thirdPlaceCountLoading}
              onChange={e => setThirdPlaceCountState(parseInt(e.target.value, 10) || 1)}
              className="w-24 bg-gray-800 border border-gray-700 text-white rounded px-3 py-2 text-sm disabled:opacity-50"
            />
            <span className="text-gray-400 text-sm">teams</span>
            <button
              onClick={handleThirdPlaceCountSave}
              disabled={thirdPlaceCountLoading || thirdPlaceCountSaving || thirdPlaceCount <= 0}
              className="py-2 px-4 rounded text-sm font-medium border bg-blue-600 border-blue-500 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {thirdPlaceCountSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-2">How many third-place group teams advance to the next round (e.g. 4 for WC2026 R32).</p>
        </section>

        <hr className="border-gray-800 mb-8" />

        <div className="bg-gray-900 p-6 rounded-lg border border-gray-800">
          <h3 className="text-white font-bold mb-4 text-lg">Data Management</h3>
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            onClick={handleExport}
            disabled={loading}
          >
            {loading ? 'Exporting...' : 'Export Database (CSV ZIP)'}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default GeneralAdminPage;